(() => {
  const ACTIVE_CLASS = 'is-visible';
  const instances = new WeakMap();

  const toNumber = (value, fallback = 0) => {
    const number = parseInt(value, 10);
    return Number.isNaN(number) ? fallback : number;
  };

  const withSectionParam = (url, sectionId) => {
    const target = new URL(url, window.location.origin);
    target.searchParams.set('section_id', sectionId);
    return target.toString();
  };

  const buildCurrentFilteredPageUrl = (page) => {
    const target = new URL(window.location.href);
    target.searchParams.set('page', page);
    return target.toString();
  };

  const pageFromUrl = (url, fallback) => {
    const target = new URL(url, window.location.origin);
    return toNumber(target.searchParams.get('page'), fallback);
  };

  const productKey = (item) => {
    const link = item.querySelector('a[href*="/products/"]');
    return link ? link.getAttribute('href').split('?')[0] : '';
  };

  const getVisibleItemsCount = (grid) => grid ? grid.querySelectorAll(':scope > .grid__item').length : 0;

  const initContainer = (container) => {
    if (!container) return;

    const existing = instances.get(container);
    if (existing) existing.destroy();

    const grid = container.querySelector('#product-grid');
    const sentinel = container.querySelector('[data-infinite-sentinel]');
    const nextLink = container.querySelector('[data-next-page-link]');
    const status = container.querySelector('[data-infinite-status]');

    if (!grid || !sentinel || !nextLink) {
      instances.delete(container);
      return;
    }

    const sectionId = grid.dataset.id;
    let nextUrl = buildCurrentFilteredPageUrl(pageFromUrl(nextLink.getAttribute('href'), 2));
    let currentPage = Math.max(1, pageFromUrl(nextUrl, 2) - 1);
    let loading = false;
    let observer;

    const setStatus = (visible, text) => {
      if (!status) return;
      if (text) status.textContent = text;
      status.classList.toggle(ACTIVE_CLASS, visible);
    };

    const cleanup = () => {
      if (observer) observer.disconnect();
      window.removeEventListener('scroll', triggerIfNeeded);
      window.removeEventListener('resize', triggerIfNeeded);
      instances.delete(container);
    };

    const finish = () => {
      setStatus(false);
      if (!nextUrl) {
        cleanup();
        sentinel.remove();
      }
    };

    const isNearViewport = () => {
      const rect = sentinel.getBoundingClientRect();
      return rect.top <= window.innerHeight + 300;
    };

    const appendIncomingProducts = (incomingGrid) => {
      if (!incomingGrid) return 0;

      let appended = 0;
      const existingKeys = new Set(
        Array.from(grid.querySelectorAll(':scope > .grid__item'))
          .map(productKey)
          .filter(Boolean)
      );

      Array.from(incomingGrid.children).forEach((item) => {
        const key = productKey(item);
        if (key && existingKeys.has(key)) return;
        grid.appendChild(item);
        if (key) existingKeys.add(key);
        appended += 1;
      });

      return appended;
    };

    const loadMore = async () => {
      if (loading || !nextUrl || !sectionId) return;

      loading = true;
      setStatus(true, 'Meer producten laden...');

      try {
        let appendedCount = 0;
        let guard = 0;

        while (nextUrl && appendedCount === 0 && guard < 20) {
          guard += 1;
          const response = await fetch(withSectionParam(nextUrl, sectionId), {
            credentials: 'same-origin'
          });
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const incomingGrid = doc.querySelector('#product-grid');
          const incomingNextLink = doc.querySelector('[data-next-page-link]');

          appendedCount += appendIncomingProducts(incomingGrid);
          currentPage = pageFromUrl(nextUrl, currentPage + 1);
          nextUrl = incomingNextLink
            ? buildCurrentFilteredPageUrl(pageFromUrl(incomingNextLink.getAttribute('href'), currentPage + 1))
            : '';
        }

        if (typeof initButtons === 'function') initButtons();
        if (typeof initButtonsCompare === 'function') initButtonsCompare();

        if (!nextUrl) {
          setStatus(true, 'Alle producten zijn geladen.');
          window.setTimeout(() => finish(), 900);
        } else {
          setStatus(false);
        }
      } catch (error) {
        console.error('Infinite scroll failed', error);
        setStatus(true, 'Nieuwe producten laden lukt nu even niet.');
      } finally {
        loading = false;
        if (nextUrl && isNearViewport()) loadMore();
      }
    };

    const triggerIfNeeded = () => {
      if (isNearViewport()) loadMore();
    };

    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) loadMore();
      });
    }, {
      rootMargin: '300px 0px'
    });

    observer.observe(sentinel);
    window.addEventListener('scroll', triggerIfNeeded, { passive: true });
    window.addEventListener('resize', triggerIfNeeded);
    instances.set(container, { destroy: cleanup, loadMore });
    triggerIfNeeded();
  };

  const initAll = () => {
    document.querySelectorAll('#ProductGridContainer').forEach(initContainer);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('collection:updated', initAll);
  document.addEventListener('shopify:section:load', initAll);
})();
